import { computed, effect, Injectable, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { PersonSearchService } from 'src/app/_services/person-search.service';
import { AggregationRequest } from 'src/app/common-interfaces-classes/AggregationRequest';
import { FilterStoreService } from './filter-store.service';
import { LocalStorageService } from 'src/app/_services/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AggregateRequestStoreService {

  // status + data used to be two independent signals, each written with its
  // own .set() call. Consumers reading both inside an effect could observe
  // one updated tick before the other, which fired downstream effects twice.
  // Combining them into a single signal makes the update atomic: there is
  // now exactly one write, so any effect depending on this state runs once.
  private result = signal<{ status: 'idle' | 'loading' | 'success' | 'error'; data: any[] }>({
    status: 'idle',
    data: [],
  });
  status = computed(() => this.result().status);
  data = computed(() => this.result().data);
  // Use this from consumers that need to react to status+data together
  // (e.g. in an effect) instead of reading status() and data() separately.
  resultState = computed(() => this.result());

  private aggregateRequest = signal<AggregationRequest>({} as AggregationRequest);
   private shouldFetch = signal(false);
   loading = signal(false);
  userQuery = signal<string>('');
  totalResults = signal<number>(0);
  startIndex = signal<number>(0);
  profilesPerPage = signal<number>(25);
  talentdiscoveryId = signal<any>(null);
  searchScreeningCriteria = signal<any>(null);
  activeView = signal<'profiles' | 'insights'>('profiles');
  insightsData = signal<any>(null);
   candidateLoaded = signal<boolean>(false);

  // Set by fetchData() when the recommend response indicates the current user's
  // account doesn't have access to Europe-based candidates — either the backend
  // returns an explicit "Continent(s) access denied" message, or a Europe-only
  // search comes back with zero results while hasEuAccess() is false. Consumers
  // (e.g. hire-candidate-list) read this to show a distinct empty state instead
  // of the generic "no results, add filters" one.
  euAccessDenied = signal<boolean>(false);

  // Whether the current user's plan/account has access to European candidates.
  // Defaults to true (no restriction assumed) until wired up to wherever this
  // app tracks that permission — call setHasEuAccess() from there once known.
  hasEuAccess = this.localStorageService.getItem('hasEUPersonSearchAccess') === 1 ? signal(true) : signal(false);

  setHasEuAccess(value: boolean) {
    this.hasEuAccess.set(value);
  }

  // Tracks the currently in-flight getPersonSearchRecommendCandidates() call.
  // Unsubscribing an HttpClient-backed Observable aborts the underlying
  // request, so this lets a fresh fetchData() cancel a still-pending one
  // (e.g. user changes screening criteria and hits "Explore Candidates"
  // again before the previous request has resolved).
  private currentRequestSubscription: Subscription | null = null;



  constructor(private personSearchService: PersonSearchService, private route: ActivatedRoute, private filterStoreService: FilterStoreService, private localStorageService: LocalStorageService) {
    effect(() => {
      const req = this.aggregateRequest(); // ← reactive dependency
      const canFetch = this.shouldFetch();
      if (!req || !canFetch || this.loading()) return;
      this.fetchData(req);        // ← API called automatically
    });
  }

  initHireRequest() {
    this.aggregateRequest.set({
       startindex: 0,
            resultsize: 20,
            searchviaboolean: true,
            booleanquery: "*",
            iscurrent: false,
            currentcompaniesonly: false,
            pastcompaniesonly: false,
            jobtitles: [],
            pastjobtitles: [],
            exclude_pastjobtitles: [],
            preferredskills: [],
            mandatoryskills: [],
            companies: [],
            industries: [],
            exclusionlist: [],
            genders: [],
            seniority: [],
            schools: [],
            schooltypes: [],
            educationaldegrees: [],
            educationalmajors: [],
            candidateshavingemails: false,
            candidateshavingcontactnumbers: false,
            minexperience: null,
            maxexperience: null,
            minexperienceinccurrentcompany: null,
            zipcodes: [],
            companysizes: [],
            candidateshavingcontacts: false,
            companytype: 'Current',
            jobtitletype: 'Current',
            industrytype: 'Current',
            experiencetype: 'Current',
            fromyearsofexperience: [],
            toyearsofexperience: [],
            locations: [],
            continentstobeincluded: [],
            functions: [],
            fullnames: [],
            currentfromyearsofexperience: [],
            currenttoyearsofexperience: [],

            schoollocation: [],
            exclude_schoollocation: [],
            needcuratedtalentpooltags: true,
            distancefromselectedlocations: 100,
            distancefromselectedlocationsmeasurementunit: 'kilometers',

            profilehighlights: [],
            positionhierarchies: [],

    } as AggregationRequest);
  }

    setAggregateRequest(data: Partial<AggregationRequest>) {
       this.shouldFetch.set(false);
        this.aggregateRequest.update(current => ({ ...current, ...data }));
        this.shouldFetch.set(true);
    }

    getAggregateRequest() {
      return this.aggregateRequest();
    }

    fetchData(req: AggregationRequest) {
      // A previous call (e.g. from before the criteria were changed) may
      // still be in flight — cancel it so its response can never land after
      // this newer one and clobber the results/loading state.
      this.currentRequestSubscription?.unsubscribe();

      this.candidateLoaded.set(true);
      this.euAccessDenied.set(false); // clear any stale flag from a previous search
      this.result.update(current => ({ ...current, status: 'loading' })); // ADD THIS

      req.calledfrom = 'listing';
      req.startindex = this.startIndex();
      req.resultsize = this.profilesPerPage();
      req.module = 'personsearch';
      delete req.aggregatetype;
      if(req?.talentdiscoveryhistoryid == null) {
        delete req.talentdiscoveryhistoryid;
      }
      req.searchviaboolean = true;
      req.savehistory = true;
      req.searchedfrom = this.route.snapshot.queryParams['type'] == 'nl-search' ? 'job-description' : this.route.snapshot.queryParams['type'];
      if(this.route.snapshot.queryParams['redirectSource'] == 'company-insights' || this.route.snapshot.queryParams['redirectSource'] == 'talent-insights') {
        delete req.searchedfrom;
        delete req.calledfrom;
      }
 this.currentRequestSubscription = this.personSearchService.getPersonSearchRecommendCandidates(req).subscribe({
      next: (res) => {
        this.candidateLoaded.set(false);

        const results = res?.data?.results ?? [];
        // A Europe-only search that comes back empty is ambiguous on its own
        // (could just be zero matches) — only treat it as an access issue when
        // combined with a known lack of EU access.
        const isEuropeOnlySearch = req.continentstobeincluded?.length === 1 && req.continentstobeincluded[0] === 'europe';
        const euDenied = res?.message === 'Continent(s) access denied'
          || (results.length === 0 && isEuropeOnlySearch && !this.hasEuAccess());

        this.euAccessDenied.set(euDenied);
        this.result.set({ status: 'success', data: euDenied ? [] : results });
        this.totalResults.set(euDenied ? 0 : (res?.data?.totalresultscount ?? 0));
        this.talentdiscoveryId.set(res?.data?.talentdiscoveryhistoryid ?? null);
        this.filterStoreService.setFilter({ talentdiscoveryhistoryid: res?.data?.talentdiscoveryhistoryid ?? null });
      },
      error: (err) => {
        console.error('Error fetching data:', err);
        this.candidateLoaded.set(false);
        this.result.set({ status: 'error', data: this.data() });
      }
    });
    }

updateCandidate(id: string, patch: any) {
  this.result.update(current => ({
    ...current,
    data: current.data.map(candidate =>
      candidate.id === id ? { ...candidate, ...patch } : candidate
    ),
  }));
}

// in AggregateRequestStoreService
resetAggregateRequest() {
  this.currentRequestSubscription?.unsubscribe();
  this.shouldFetch.set(false);
  this.euAccessDenied.set(false);
  this.aggregateRequest.set({} as AggregationRequest); // full replace, not merge
  // don't set shouldFetch back to true
  // result is a root-singleton signal that otherwise survives navigation - without this,
  // a freshly-mounted HireCandidateListComponent's resultState() effect sees the PREVIOUS
  // search's still-'success' candidate list on its very first run, firing addMetaData()/
  // scoring calls against stale ids before this search's own fetch has even started.
  this.result.set({ status: 'idle', data: [] });
}


}
